class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.mainSearchCachedResults = {};
    this.predictiveSearchCachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.sectionId = this.dataset.sectionId;
    this.setupEventListeners();

    document.addEventListener("DOMContentLoaded", () => {
      document
        .querySelector(".search-desktop-wrapper__close")
        .addEventListener("click", (event) => {
          this.close();
        });
    });
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      this.input.focus();
    }
  }

  onFocus() {
    document.documentElement.classList.add("overflow-hidden");

    // Detect mobile devices (iOS and Android)
    const isMobile = /iPhone|iPod|Android/i.test(navigator.userAgent);

    if (isMobile) {
      const preventTouchMove = (e) => e.preventDefault();
      document.documentElement.addEventListener("touchmove", preventTouchMove, {
        passive: false,
      });

      // Clean up when focus is lost
      const cleanup = () => {
        document.documentElement.removeEventListener(
          "touchmove",
          preventTouchMove,
        );
        this.removeEventListener("focusout", cleanup);
      };
      this.addEventListener("focusout", cleanup);
    }

    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    if (window.innerWidth > 990) {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]",
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]',
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();

    this.setLiveRegionLoadingState();

    if (
      this.mainSearchCachedResults[queryKey] ||
      this.predictiveSearchCachedResults[queryKey]
    ) {
      this.renderSearchResults(this.mainSearchCachedResults[queryKey]);

      setTimeout(() => {
        if (this.predictiveSearchCachedResults[queryKey]) {
          this.renderPredictiveSearchResults(
            this.predictiveSearchCachedResults[queryKey],
          );
        }
      }, 0);

      return;
    }

    fetch(
      `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent(
        "options[prefix]",
      )}=last&section_id=${this.sectionId}`,
      {
        signal: this.abortController.signal,
      },
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector(`#shopify-section-${this.sectionId}`).innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.mainSearchCachedResults[queryKey] =
              resultsMarkup;
          },
        );
        this.renderSearchResults(resultsMarkup);

        fetch(
          `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=query,collection&section_id=predictive-search`,
        )
          .then((response) => {
            if (!response.ok) {
              var error = new Error(response.status);
              this.close();
              throw error;
            }

            return response.text();
          })
          .then((text) => {
            const resultsMarkup = new DOMParser()
              .parseFromString(text, "text/html")
              .querySelector(`#shopify-section-predictive-search`);
            // Save bandwidth keeping the cache in all instances synced
            this.allPredictiveSearchInstances.forEach(
              (predictiveSearchInstance) => {
                predictiveSearchInstance.predictiveSearchCachedResults[
                  queryKey
                ] = resultsMarkup;
              },
            );

            this.renderPredictiveSearchResults(resultsMarkup);
          })
          .catch((error) => {
            console.error("Error fetching queries and collections:", error);
          });
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (typeof resultsMarkup !== "string") {
      resultsMarkup = resultsMarkup?.querySelector(
        `#predictive-search-results`,
      )?.innerHTML;
    }

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    document.dispatchEvent(new CustomEvent("applyColorBadges"));
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  renderPredictiveSearchResults(predictiveSearchResults) {
    const suggestionsWrapper = this.predictiveSearchResults?.querySelector(
      ".predictive-search__suggestions-wrapper",
    );
    const noProducts = this.predictiveSearchResults
      ?.querySelector("#predictive-search-results")
      .getAttribute("data-no-products");
    const suggestionGroup = this.predictiveSearchResults?.querySelector(
      ".predictive-search__result-group--suggestions",
    );

    const predictiveSuggestionsWrapper = predictiveSearchResults.querySelector(
      ".predictive-search__suggestions-wrapper",
    );

    if (predictiveSuggestionsWrapper && suggestionsWrapper) {
      suggestionsWrapper.innerHTML = predictiveSuggestionsWrapper.innerHTML;

      if (
        predictiveSuggestionsWrapper.getAttribute(
          "data-is-hidden-tablet-and-down",
        ) === "false"
      ) {
        suggestionsWrapper?.classList?.remove("hide-tablet-and-down");
        suggestionGroup?.classList?.remove("hidden");
      } else {
        suggestionsWrapper?.classList?.add("hide-tablet-and-down");
        if (noProducts === "true") {
          suggestionGroup?.classList?.add("hidden");
        } else {
          suggestionGroup?.classList?.remove("hidden");
        }
      }
    }
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        ?.textContent,
    );
  }

  getResultsMaxHeight() {
    const sectionHeader = document.querySelector(".section-header");
    const sectionHeaderHeight =
      sectionHeader instanceof HTMLElement
        ? sectionHeader.offsetHeight
        : undefined;

    const announcementBar = document.querySelector(".announcement-bar-section");
    const announcementBarHeight =
      announcementBar instanceof HTMLElement
        ? announcementBar.offsetHeight
        : undefined;

    const calculatedResultsMaxHeight =
      window.innerHeight -
      sectionHeaderHeight -
      64 -
      (announcementBarHeight || 0);

    this.resultsMaxHeight = calculatedResultsMaxHeight
      ? `${calculatedResultsMaxHeight}px`
      : false;

    return this.resultsMaxHeight;
  }

  open() {
    document.documentElement.classList.add("overflow-hidden");
    this.predictiveSearchResults.style.setProperty(
      "--max-height",
      this.resultsMaxHeight || this.getResultsMaxHeight(),
    );

    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    document.documentElement.classList.remove("overflow-hidden");
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }
}

customElements.define("predictive-search", PredictiveSearch);
